import { useEffect } from 'react';
import { Loader, Text, useNotify } from '@hotjar/hotsauce';
import { useSettings } from '@hotjar/settings';
import type {
  FilterKindEnum,
  GlobalFilter,
} from '../../../domains/global-filters/model/FilterChain';
import type { AddFilterTippyInstance } from '../../StatelessComponent/AddFilter/AddFilter';
import type { ToolName, ProductType } from '../../../domains/global-filters/model/Product';
import type { BaseSite, SiteWithAccess, SiteWithFeature } from '../../../domains/site/model';
import type { MinimalDateConstraint } from '../../StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import { GlobalFilterContextObject } from '../GlobalContext';
import { StatefulFilterChainWithResources } from '../FilterChain/StatefulFilterChainWithResources';

export type ContextOfUse = {
  productType: ProductType;
  page: string;
  toolName: ToolName;
};

export type StatefulFunnelStepProps = {
  currentStepInUiFormat?: GlobalFilter;
  currentSite?: BaseSite & SiteWithAccess & SiteWithFeature;
  currentSiteIsError?: boolean;
  onChange: (filterInUiFormat: GlobalFilter) => void;
  onUpsellRequest: (featureName: string, kind?: string) => void;
  setSelectStepTippyInstance?: (tippyInstance: AddFilterTippyInstance | null) => void;
  dateConstraint?: MinimalDateConstraint;
  supportedFilterList: Set<FilterKindEnum>;
  contextOfUse: ContextOfUse;
  isFiltersDropdownVisibleByDefault?: boolean;
  autoFocusAddFilter?: boolean;
  contextObject: GlobalFilterContextObject;
};

export const StatefulFunnelStep = ({
  currentStepInUiFormat,
  currentSite,
  currentSiteIsError,
  onChange,
  onUpsellRequest,
  setSelectStepTippyInstance,
  dateConstraint,
  supportedFilterList,
  contextOfUse,
  isFiltersDropdownVisibleByDefault,
  autoFocusAddFilter,
  contextObject,
}: StatefulFunnelStepProps): JSX.Element => {
  const { showToast } = useNotify();
  // We need to be sure settings is loaded to check for getMinimumFeatureGroupForFeature
  const { isLoading: isSettingsLoading } = useSettings();
  useEffect(() => {
    if (currentSiteIsError === true) {
      showToast({ kind: 'danger', text: 'Error when fetching current site' });
    }
  }, [currentSiteIsError, showToast]);

  if (currentSiteIsError === true) {
    return <Text textColor="text_error">Error when fetching current site...</Text>;
  }

  if (currentSite == null || isSettingsLoading) {
    return <Loader size="m" />;
  }

  return (
    <GlobalFilterContextObject.Provider value={contextObject}>
      <StatefulFilterChainWithResources
        currentFiltersInUiFormat={currentStepInUiFormat}
        currentSite={currentSite}
        onChange={onChange}
        onUpsellRequest={onUpsellRequest}
        setAddFilterTippyInstance={setSelectStepTippyInstance}
        userAttributeOptionMap={new Map()}
        dateConstraint={dateConstraint}
        supportedFilterList={supportedFilterList}
        contextOfUse={contextOfUse}
        isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
        autoFocusAddFilter={autoFocusAddFilter}
        hideDateRangeFilter
        hideSaveSegment
        doNotTrackFiltersChange
        hideAddFilters={currentStepInUiFormat && currentStepInUiFormat.filterList.length > 0}
        hideClearAllFilters
        isFunnelStep
      />
    </GlobalFilterContextObject.Provider>
  );
};
